import React, { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import MainSwitch from "@/components/MainSwitch";
import ChatAssistant from "@/components/ChatAssistant";
import CombinedDashboard from "@/components/CombinedDashboard";
import Part4Intraday from "@/pages/Part4Intraday";
import Part2USIntraday from "@/pages/Part2USIntraday";
import Paywall from "@/pages/Paywall";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import AdminPanel from "@/pages/AdminPanel";
import { subMe } from "@/lib/api";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import "@/App.css";

function Shell() {
  const { user, loading } = useAuth();
  const [active, setActive] = useState(1);
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    if (!user) { setSubStatus(null); return; }
    if (user.is_admin) { setSubStatus("active"); return; }
    subMe().then(m => setSubStatus(m.status || "none")).catch(() => setSubStatus("none"));
  }, [user?.user_id, user?.is_admin]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" /></div>;
  }
  if (!user) return <Login />;
  if (subStatus === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" /></div>;
  }
  if (subStatus !== "active") return <Paywall onUnlocked={() => setSubStatus("active")} />;

  // Non-admins default to Intraday / Options; only admins get admin tab
  const safeActive = (active === 3 && !user.is_admin) ? 1 : active;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body relative">
      <div className="fixed inset-0 bg-grid opacity-50 pointer-events-none" />
      <MainSwitch active={safeActive} onSwitch={setActive} />
      <main className="relative max-w-[1440px] mx-auto px-4 md:px-8 pt-6 pb-24">
        {safeActive !== 3 && <CombinedDashboard />}
        <AnimatePresence mode="wait">
          <motion.div
            key={safeActive}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {safeActive === 1 && <Part4Intraday />}
            {safeActive === 2 && <Part2USIntraday />}
            {safeActive === 3 && user.is_admin && <AdminPanel compact />}
          </motion.div>
        </AnimatePresence>
      </main>
      <ChatAssistant />
      <Toaster position="top-right" theme="dark" richColors offset={80} />
    </div>
  );
}

export default function App() {
  const isAuthCallback = typeof window !== "undefined" && window.location.hash?.includes("session_id=");
  return (
    <AuthProvider>
      {isAuthCallback ? <AuthCallback /> : <Shell />}
    </AuthProvider>
  );
}
